import React, { useCallback, useEffect, useRef, useState } from "react";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import { Skeleton } from "@mui/material";

import House from "../../Assets/images/house.png";
import getServices from "../../Services/get-service";
import postService from "../../Services/post-service";
import Constant from "../../Constant";

const PAGE_SIZE = 20;
const TABS = ["All", "Sales", "Rentals"];

// Format properties returned by the API.
const mapProperty = (p) => {
  const investmentType =
    typeof p.investmentType === "string"
      ? p.investmentType.trim().toUpperCase()
      : null;

  const remainingUnits =
    p.unitRemaining == null || p.unitRemaining === "null" ? 0 : p.unitRemaining;

  return {
    ...p,

    image: p.propertyImages?.[0]?.imageUrl || House,

    location: p.locality,

    type: investmentType,

    typeColor:
      investmentType === "SALES"
        ? "bg-[#00BC67] text-white"
        : "bg-[#007BFF] text-white",

    price: `₦${Constant.FormatNumber(p.price ?? 0)}`,

    percentSold: p.percentageSold ?? 0,

    target: `₦${Constant.FormatNumber(p.price ?? 0)}`,

    raised: `₦${Constant.FormatNumber(p.amountRaised ?? 0)}`,

    unitsRemaining: `${remainingUnits} units remaining`,

    pricePerUnit: `₦${Constant.FormatNumber(p.unitPrice ?? 0)}`,

    unitsLeft: remainingUnits,

    targetDetail: `₦${Constant.FormatNumber(p.price ?? 0)}`,

    percentSoldDetail: p.percentageSold ?? 0,
  };
};

function InvestmentListTabs({ onPropertyClick }) {
  const [activeTab, setActiveTab] = useState("All");

  // Backend pagination starts from page 0.
  const [currentPage, setCurrentPage] = useState(0);

  const [properties, setProperties] = useState([]);

  const [bookmarkedIds, setBookmarkedIds] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [hasNextPage, setHasNextPage] = useState(false);

  // Cache backend pages to avoid repeated requests.
  const pageCache = useRef(new Map());

  // Fetch a specific backend page.
  const fetchBackendPage = useCallback((page) => {
    if (pageCache.current.has(page)) {
      return pageCache.current.get(page);
    }

    const request = getServices
      .userGetProperty(PAGE_SIZE, page)
      .then((response) => {
        const result = response.data;

        if (result?.statusCode !== 200 || !Array.isArray(result?.data)) {
          throw new Error(result?.description || "Unable to load properties.");
        }

        return result.data;
      })
      .catch((error) => {
        // Failed requests should be retryable.
        pageCache.current.delete(page);

        throw error;
      });

    pageCache.current.set(page, request);

    return request;
  }, []);

  // Fetch properties whenever the tab or page changes.
  useEffect(() => {
    let cancelled = false;

    const fetchProperties = async () => {
      setLoading(true);
      setError("");

      try {
        let result = [];
        let hasNext = false;

        if (activeTab === "All") {
          // server-side pagination.
          const data = await fetchBackendPage(currentPage);

          result = data;

          if (data.length === PAGE_SIZE) {
            const nextPageData = await fetchBackendPage(currentPage + 1);

            hasNext = nextPageData.length > 0;
          }
        } else {
          // Sales/Rentals:
          const selectedType = activeTab.toUpperCase();

          const startIndex = currentPage * PAGE_SIZE;
          const requiredCount = startIndex + PAGE_SIZE + 1;

          const matchingProperties = [];

          let backendPage = 0;

          while (matchingProperties.length < requiredCount) {
            const data = await fetchBackendPage(backendPage);

            const matches = data.filter(
              (property) =>
                property.investmentType?.trim().toUpperCase() === selectedType,
            );

            matchingProperties.push(...matches);
            if (data.length < PAGE_SIZE) {
              break;
            }

            backendPage += 1;
          }

          result = matchingProperties.slice(startIndex, startIndex + PAGE_SIZE);

          hasNext = matchingProperties.length > startIndex + PAGE_SIZE;
        }

        if (cancelled) return;

        if (result.length === 0 && currentPage > 0) {
          setCurrentPage((previous) => previous - 1);
          return;
        }

        setProperties(result.map(mapProperty));

        setHasNextPage(hasNext);
      } catch (err) {
        if (cancelled) return;

        setProperties([]);
        setHasNextPage(false);

        setError(
          err?.response?.data?.description ||
            err?.message ||
            "Unable to load properties. Please try again.",
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchProperties();

    return () => {
      cancelled = true;
    };
  }, [activeTab, currentPage, fetchBackendPage]);

  useEffect(() => {
    let cancelled = false;

    const fetchSavedProperties = async () => {
      try {
        const response = await getServices.getSavedProduct(100, 0);

        if (cancelled || response?.data?.statusCode !== 200) {
          return;
        }

        const rawSaved = response.data.data;

        const savedList =
          [
            rawSaved,
            rawSaved?.savedProperties,
            rawSaved?.content,
            rawSaved?.projects,
            rawSaved?.data,
          ].find(Array.isArray) || [];

        const savedIds = savedList
          .map(
            (item) =>
              item.propertyId ??
              item.projectId ??
              item.id ??
              item.property?.id ??
              item.project?.id,
          )
          .filter((id) => id != null)
          .map(String);

        setBookmarkedIds(savedIds);
      } catch (error) {
        console.error("Unable to fetch saved properties:", error);
      }
    };

    fetchSavedProperties();

    return () => {
      cancelled = true;
    };
  }, []);

  // Bookmark handling.
  const handleBookmarkToggle = (event, propertyId) => {
    event.stopPropagation();

    const id = String(propertyId);

    if (bookmarkedIds.includes(id)) {
      setBookmarkedIds((previous) =>
        previous.filter((savedId) => savedId !== id),
      );

      postService.removePropertyBuyLater(id).catch(() => {
        setBookmarkedIds((previous) =>
          previous.includes(id) ? previous : [...previous, id],
        );
      });
    } else {
      setBookmarkedIds((previous) =>
        previous.includes(id) ? previous : [...previous, id],
      );

      postService.addPropertyBuyLater(id).catch(() => {
        setBookmarkedIds((previous) =>
          previous.filter((savedId) => savedId !== id),
        );
      });
    }
  };

  // Change tabs and reset pagination.
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;

    setActiveTab(tab);
    setCurrentPage(0);
    setProperties([]);
    setHasNextPage(false);
  };

  const handleNextPage = () => {
    if (loading || !hasNextPage) return;

    setCurrentPage((previous) => previous + 1);
  };

  const handlePreviousPage = () => {
    if (loading || currentPage === 0) return;

    setCurrentPage((previous) => Math.max(0, previous - 1));
  };

  return (
    <div className="my-6">
      {/* Tabs */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabChange(tab)}
              aria-pressed={activeTab === tab}
              className={`rounded-xl px-4 py-2 text-[12px] font-[600] transition-colors ${
                activeTab === tab
                  ? "bg-primary-color text-white"
                  : "bg-[#E6F8F0] text-black"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <div
              key={index}
              className="overflow-hidden rounded-xl border border-[#E6E6E7] bg-white p-4"
            >
              <Skeleton
                variant="rectangular"
                height={200}
                className="mb-4 rounded-lg"
              />

              <Skeleton variant="text" width="80%" height={24} />

              <Skeleton
                variant="text"
                width="50%"
                height={20}
                className="mb-4"
              />

              <Skeleton
                variant="rectangular"
                height={10}
                className="mb-3 rounded-full"
              />

              <div className="flex justify-between">
                <Skeleton variant="text" width="30%" height={20} />
                <Skeleton variant="text" width="30%" height={20} />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        // Error state.
        <p role="alert" className="py-12 text-center text-red-600">
          {error}
        </p>
      ) : properties.length === 0 ? (
        // Empty state.
        <p className="py-12 text-center text-[#6B7281]">
          No properties available in this category.
        </p>
      ) : (
        <>
          {/* Properties */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {properties.map((prop) => (
              <div
                key={prop.id}
                className="cursor-pointer overflow-hidden rounded-xl border border-[#E6E6E7] bg-white transition-shadow hover:shadow-md"
                onClick={() => onPropertyClick?.(prop)}
              >
                <div className="relative h-[200px] w-full">
                  <img
                    src={prop.image}
                    alt={prop.title}
                    className="h-full w-full object-cover"
                  />

                  {/* Investment type */}
                  {prop.type && (
                    <span
                      className={`absolute left-3 top-3 rounded-md px-3 py-1 text-[12px] font-[500] ${prop.typeColor}`}
                    >
                      {prop.type}
                    </span>
                  )}

                  {/* Bookmark */}
                  <button
                    type="button"
                    className="absolute right-3 top-3 rounded-full bg-white p-1.5 shadow-sm hover:bg-gray-100"
                    onClick={(event) => handleBookmarkToggle(event, prop.id)}
                    aria-label={
                      bookmarkedIds.includes(String(prop.id))
                        ? `Remove ${prop.title} from saved properties`
                        : `Save ${prop.title}`
                    }
                    aria-pressed={bookmarkedIds.includes(String(prop.id))}
                  >
                    {bookmarkedIds.includes(String(prop.id)) ? (
                      <BookmarkIcon sx={{ fontSize: 20, color: "#00BC67" }} />
                    ) : (
                      <BookmarkBorderOutlinedIcon
                        sx={{ fontSize: 20, color: "#808285" }}
                      />
                    )}
                  </button>
                </div>

                <div className="p-4">
                  <h3 className="mb-1 text-[16px] font-bold text-back-color2">
                    {prop.title}
                  </h3>

                  <p className="mb-4 flex items-center text-[12px] text-[#808285]">
                    <LocationOnOutlinedIcon
                      sx={{ fontSize: 16, marginRight: "4px" }}
                    />
                    {prop.location}
                  </p>

                  {/* Percentage sold */}
                  <div className="mb-2">
                    <p className="mb-1 text-[12px] font-bold text-back-color2">
                      {prop.percentSold}% sold
                    </p>

                    <div className="h-1.5 w-full rounded-full bg-gray-200">
                      <div
                        className="h-1.5 rounded-full bg-primary-color"
                        style={{
                          width: `${Math.max(
                            0,
                            Math.min(100, Number(prop.percentSold) || 0),
                          )}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Investment amount */}
                  <div className="mb-3 flex items-center justify-between border-gray-100 pb-3">
                    <div>
                      <p className="text-[14px] font-[400] text-[#6B7281]">
                        Target
                      </p>

                      <p className="text-[16px] font-[700] text-black">
                        {prop.target}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-[14px] font-[400] text-[#6B7281]">
                        Raised
                      </p>

                      <p className="text-[16px] font-[700] text-black">
                        {prop.raised}
                      </p>
                    </div>
                  </div>

                  <p className="text-[14px] font-[400] text-[#6B7281]">
                    {prop.unitsRemaining}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {(currentPage > 0 || hasNextPage) && (
            <nav
              aria-label="Property pagination"
              className="mt-8 flex items-center justify-center gap-3 sm:justify-end"
            >
              <button
                type="button"
                onClick={handlePreviousPage}
                disabled={loading || currentPage === 0}
                className="rounded-lg border border-[#E6E6E7] px-4 py-2 text-sm font-medium transition-colors hover:bg-[#00BC67] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>

              <span className="text-sm text-[#6B7281]">{currentPage + 1}</span>

              <button
                type="button"
                onClick={handleNextPage}
                disabled={loading || !hasNextPage}
                className="rounded-lg border border-[#E6E6E7] px-4 py-2 text-sm font-medium transition-colors hover:bg-[#00BC67] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

export default InvestmentListTabs;
